import { useEffect, useMemo, useRef, useState } from "react"
import { skipToken } from "@reduxjs/toolkit/query/react"
import {
  IconAlertTriangle,
  IconBuildingOff,
  IconDeviceFloppy,
  IconLoader2,
  IconMapPinShare,
  IconSwitchHorizontal,
  IconUserOff,
  IconX,
} from "@tabler/icons-react"
import type { ComponentType } from "react"
import { Navigate } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { useGetTeamsQuery } from "@/app/booking-calendar/bookingCalendarApi"
import { AffectedBookingsList } from "@/app/booking-calendar/components/AffectedBookingsList"
import { type CalendarDate, SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import {
  type EventTypeSlug,
  useCreateScheduleOverrideMutation,
  useGetResolveCapabilitiesQuery,
  usePreviewScheduleOverrideQuery,
} from "../../schedule-overrides/scheduleOverridesApi"
import {
  affectsBookings,
  previewFor,
  SITE_CLOSURE,
  useDebouncedValue,
} from "../../schedule-overrides/lib/overridePreview"
import {
  DAY_START,
  formatDateRange,
  NOTE_MAX_LENGTH,
  SLOT_TYPE_EVENT_CHOICES,
} from "../../schedule-overrides/lib/overrideForm"
import { releaseNotes } from "../../schedule-overrides/lib/releaseNotice"
import {
  buildOverrideRequest,
  CHECKING_OPTION_REASON,
  describeCreateError,
  needsResolution,
  OVERRIDE_EDIT_KIND,
  OVERRIDE_EDIT_LABEL,
  type OverrideEditContext,
  type OverrideEditForm,
  type OverrideEditKind,
  overrideEditErrors,
  overrideEditFields,
  reassignmentTargets,
  resolutionOptions,
  type ResolutionOptionId,
  windowTouchesWeek,
  withStart,
} from "../lib/overrideEditModel"
import { ResolutionChoiceDialog } from "./ResolutionChoiceDialog"
import { AutomaticReassignmentDialog } from "../../components/AutomaticReassignmentDialog"
import { withServerState } from "../../components/automaticReassignment"
import type { BookingDetails } from "@/app/booking-calendar/bookingCalendarApi"
import { manualReschedulePath } from "../resolve/paths"

const ICON: Record<OverrideEditKind, ComponentType<{ className?: string }>> = {
  siteClosure: IconBuildingOff,
  eventTypeOverride: IconSwitchHorizontal,
  unavailable: IconUserOff,
  reassignment: IconMapPinShare,
}

/** The override buttons above the week (AB#806): one per kind the selected schedule offers. */
export function OverrideEditButtons({ kinds, onStart }: { kinds: readonly OverrideEditKind[]; onStart: (kind: OverrideEditKind) => void }) {
  return (
    <>
      {kinds.map(kind => {
        const Icon = ICON[kind]
        return (
          <Button key={kind} variant="outline" size="sm" onClick={() => onStart(kind)} data-override-button={kind}>
            <Icon className="size-4" />
            {OVERRIDE_EDIT_LABEL[kind]}
          </Button>
        )
      })}
    </>
  )
}

/**
 * The override edit state's form (AB#806): when, and what each kind needs, with the server's dry run
 * for the form as it stood once typing stopped — its refusals (the one-site-per-host refusal among
 * them), what it would write, and the booked appointments it would leave in place, each with its
 * quick edits. The pending window itself is drawn on the grid by SlotPlanEditor.
 *
 * Save creates the override for the selected schedule. When the kind affects bookings and the dry
 * run lists any, it first asks what to do about them (the resolution choice); proceeding leaves them
 * Booked (rule 4), listed in the override's detail until each is handled. Automatic reassignment
 * (AB#810) creates the override the same way, then shows the server's plan for it and, once
 * confirmed, the batch's progress; backing out of the plan leaves the bookings Booked, as proceeding
 * does. The edit state closes when that dialog does. Manual reschedule (AB#811) creates the override
 * the same way, then goes to the manual reschedule workspace for it.
 */
export function OverrideEditPanel({
  form,
  onChange,
  context,
  monday,
  today,
  onCancel,
  onCreated,
}: {
  form: OverrideEditForm
  onChange: (form: OverrideEditForm) => void
  context: OverrideEditContext
  /** The week showing, to say when the window lies outside it. */
  monday: CalendarDate
  /** Today, Pacific: an override cannot start before it. */
  today: CalendarDate
  onCancel: () => void
  onCreated: () => void
}) {
  const { target } = context
  const fields = overrideEditFields(form.kind)
  const kind = OVERRIDE_EDIT_KIND[form.kind]
  const affects = affectsBookings(kind)

  const localErrors = overrideEditErrors(form, context, today)
  const request = useMemo(() => buildOverrideRequest(form, context), [form, context])
  const requestKey = localErrors.length === 0 && request ? JSON.stringify(request) : null

  // The form as it stood once it stopped changing for a moment: what the preview is asked about.
  const settledKey = useDebouncedValue(requestKey, 400)
  const settled = requestKey !== null && settledKey === requestKey
  const previewQuery = usePreviewScheduleOverrideQuery(settled && request ? request : skipToken)
  const preview = previewFor(settled, previewQuery)
  const previewing = settled && (!preview || previewQuery.isFetching)

  // Every other site is a reassignment target: one the host is not on yet, the server adds them to
  // (with no hours there of their own) before opening them for the window.
  const teamsQuery = useGetTeamsQuery(fields.targetSite ? undefined : skipToken)
  const targets = useMemo(
    () => reassignmentTargets(teamsQuery.data ?? [], target.teamSlug),
    [teamsQuery.data, target.teamSlug],
  )

  // Which resolutions the server offers at all (AB#810), read before Save so the choice shows the
  // server's word: "Checking…" until it answers, disabled with its reason when it refuses, and enabled
  // only when it says so. Only kinds that can ask need it.
  const capabilitiesQuery = useGetResolveCapabilitiesQuery(affects ? undefined : skipToken)
  const options = withServerState(resolutionOptions(), capabilitiesQuery.currentData, {
    loading: capabilitiesQuery.isFetching || capabilitiesQuery.isUninitialized,
    error: capabilitiesQuery.error,
    checkingReason: CHECKING_OPTION_REASON,
  })

  const [createOverride, { isLoading: saving }] = useCreateScheduleOverrideMutation()
  const [submitErrors, setSubmitErrors] = useState<string[]>([])
  const [choosing, setChoosing] = useState(false)
  // The override just created for automatic reassignment, and the bookings it was saved with (to name
  // the customers in the plan): the plan dialog is open while it is set.
  const [resolving, setResolving] = useState<{ id: string; bookings: readonly BookingDetails[] } | null>(null)
  // The override just created for manual reschedule: its workspace is navigated to while it is set.
  // A <Navigate> rather than useNavigate, so the panel still renders outside a router.
  const [manualFor, setManualFor] = useState<string | null>(null)

  useEffect(() => {
    setSubmitErrors([])
  }, [requestKey])

  // The panel opens below the week, out of sight of the button that opened it on a tall grid.
  const sectionRef = useRef<HTMLElement>(null)
  useEffect(() => {
    sectionRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" })
  }, [])

  const update = (patch: Partial<OverrideEditForm>) => onChange({ ...form, ...patch })

  const canSave = !!request && localErrors.length === 0 && !!preview && preview.errors.length === 0 && !previewing && !saving
  const bookings = preview?.bookings ?? []

  async function create(then?: "automatic" | "manual") {
    if (!request) return
    const savedWith = bookings
    setSubmitErrors([])
    try {
      const result = await createOverride(request).unwrap()
      const created = result.override?.override
      if (created?.status === 5) {
        toast.error("Override saved, but none of its days could be applied, so it is not in force.", {
          description: "Open it from the week to see why, then withdraw it and try again.",
        })
      } else if (created && (created.failedDays > 0 || created.status !== 1)) {
        toast.warning("Override saved, but some days could not be applied.", {
          description: `${created.failedDays} day(s) failed. Open it from the week to see which.`,
        })
      } else {
        toast.success("Override saved and applied.")
      }
      setChoosing(false)
      const id = result.override?.override.id
      if (then === "automatic" && id) {
        setResolving({ id, bookings: savedWith })
        return
      }
      if (then === "manual" && id) {
        setManualFor(id)
        return
      }
      onCreated()
    } catch (err) {
      setChoosing(false)
      setSubmitErrors(describeCreateError(err))
    }
  }

  function save() {
    if (!canSave) return
    if (needsResolution(affects, preview?.bookings)) {
      setChoosing(true)
      return
    }
    void create()
  }

  function choose(id: ResolutionOptionId) {
    if (id === "proceed") void create()
    if (id === "automatic" && options.some(o => o.id === "automatic" && o.enabled)) void create("automatic")
    // Opened only when the server says so (resolve/capabilities): save, then the workspace (AB#811).
    if (id === "manual" && options.some(o => o.id === "manual" && o.enabled)) void create("manual")
  }

  const serverErrors = submitErrors.length > 0 ? submitErrors : (preview?.errors ?? [])
  const title = OVERRIDE_EDIT_LABEL[form.kind]

  return (
    <section
      ref={sectionRef}
      className="flex flex-col gap-4 rounded-lg border p-4"
      aria-label={`New override: ${title}`}
      data-override-edit={form.kind}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold">New override: {title}</h2>
        <span className="text-xs text-muted-foreground">
          {target.kind === "site"
            ? "For the whole site."
            : form.kind === "reassignment"
              ? "Moves this host from this site to another for the window; every other site of theirs is closed then. A site they are not on yet adds them to its team."
              : form.allSites
                ? "For this host at every site they host at."
                : "For this host at this site."}
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="override-edit-start">Starts (Pacific)</Label>
          <Input
            id="override-edit-start"
            type="datetime-local"
            className="w-56"
            min={`${today}T${DAY_START}`}
            value={form.start}
            onChange={(e) => onChange(withStart(form, e.target.value))}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="override-edit-end">Ends (Pacific)</Label>
          <Input
            id="override-edit-end"
            type="datetime-local"
            className="w-56"
            min={form.start || `${today}T${DAY_START}`}
            value={form.end}
            onChange={(e) => update({ end: e.target.value })}
          />
        </div>
        <p className="text-xs text-muted-foreground">12:00 AM to 11:59 PM covers whole days.</p>
      </div>

      {fields.eventType && (
        <div className="flex flex-col gap-2">
          <Label>Event type to run</Label>
          <RadioGroup
            value={form.eventTypeSlug}
            onValueChange={(value) => update({ eventTypeSlug: value as EventTypeSlug })}
            className="flex flex-wrap gap-4"
            aria-label="Event type to run"
          >
            {SLOT_TYPE_EVENT_CHOICES.map(choice => (
              <Label key={choice.value} htmlFor={`override-edit-et-${choice.value}`} className="flex items-center gap-2 font-normal">
                <RadioGroupItem id={`override-edit-et-${choice.value}`} value={choice.value} aria-label={choice.label} />
                {choice.label}
              </Label>
            ))}
          </RadioGroup>
          <p className="text-xs text-muted-foreground">The other event types are closed at this site for the window.</p>
        </div>
      )}

      {fields.targetSite && (
        <div className="flex flex-col gap-2">
          <Label>Reassign to</Label>
          {teamsQuery.isLoading || (teamsQuery.isFetching && targets.length === 0) ? (
            <p className="text-sm text-muted-foreground">Loading sites…</p>
          ) : teamsQuery.isError ? (
            <p className="text-sm text-destructive">The sites could not be loaded.</p>
          ) : targets.length === 0 ? (
            <p className="text-sm text-muted-foreground">There is no other site to reassign them to.</p>
          ) : (
            <RadioGroup
              value={form.targetTeamSlug}
              onValueChange={(value) => update({ targetTeamSlug: value })}
              className="flex flex-wrap gap-4"
              aria-label="Reassign to"
            >
              {targets.map(site => (
                <Label key={site.teamSlug} htmlFor={`override-edit-target-${site.teamSlug}`} className="flex items-center gap-2 font-normal">
                  <RadioGroupItem id={`override-edit-target-${site.teamSlug}`} value={site.teamSlug} aria-label={site.name} />
                  {site.name}
                </Label>
              ))}
            </RadioGroup>
          )}
        </div>
      )}

      {fields.note !== "hidden" && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="override-edit-note">
            Note{fields.note === "optional" && <span className="font-normal text-muted-foreground"> (optional)</span>}
          </Label>
          <Textarea
            id="override-edit-note"
            value={form.note}
            maxLength={NOTE_MAX_LENGTH}
            placeholder="Shown on the staff calendar over the affected days — e.g. “Out sick”, “Snow closure”."
            onChange={(e) => update({ note: e.target.value })}
          />
          <span className="self-end text-xs text-muted-foreground">{form.note.trim().length}/{NOTE_MAX_LENGTH}</span>
        </div>
      )}

      {fields.allSites && (
        <Label htmlFor="override-edit-all-sites" className="flex items-center gap-2 font-normal">
          <Checkbox
            id="override-edit-all-sites"
            checked={form.allSites}
            onCheckedChange={(checked) => update({ allSites: checked === true })}
          />
          All of this host&apos;s sites
        </Label>
      )}

      {!windowTouchesWeek(form, monday) && localErrors.length === 0 && (
        <p className="text-xs text-muted-foreground">This window is outside the week showing; step to its week to see it on the grid.</p>
      )}

      <div className="flex flex-col gap-3 rounded-lg border bg-muted/40 p-3 text-sm" data-testid="override-edit-preview">
        {localErrors.length > 0 ? (
          <ul className="list-disc pl-5 text-muted-foreground">
            {localErrors.map(message => <li key={message}>{message}</li>)}
          </ul>
        ) : !preview && submitErrors.length === 0 ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <IconLoader2 className="size-4 animate-spin" />
            Checking…
          </div>
        ) : serverErrors.length > 0 ? (
          <div className="flex flex-col gap-1 text-destructive" role="alert">
            <span className="font-medium">This can&apos;t be made:</span>
            <ul className="list-disc pl-5">
              {serverErrors.map((message, index) => <li key={index}>{message}</li>)}
            </ul>
          </div>
        ) : preview ? (
          <>
            <div className="flex flex-col gap-1">
              <span className="font-medium">What this will change</span>
              <ul className="flex flex-col gap-1">
                {preview.lines.map(line => (
                  <li key={`${line.teamName}|${line.target}|${line.windows}`}>
                    <span className="font-medium">{line.teamName}</span> · {line.target}: {line.windows} ·{" "}
                    <span className="text-muted-foreground">
                      {line.days} {line.days === 1 ? "day" : "days"} ({formatDateRange(line.firstDate, line.lastDate)})
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {preview.warnings.length > 0 && (
              <ul className="flex flex-col gap-1">
                {preview.warnings.map(warning => (
                  <li key={warning} className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
                    <IconAlertTriangle className="mt-0.5 size-4 shrink-0" />
                    {warning}
                  </li>
                ))}
              </ul>
            )}

            {affects ? (
              <div className="flex flex-col gap-2">
                <span className="font-medium" data-testid="override-edit-booking-count">
                  {bookings.length === 0
                    ? "No booked appointments fall in this window."
                    : `${bookings.length} booked ${bookings.length === 1 ? "appointment falls" : "appointments fall"} in this window.`}
                </span>
                {bookings.length > 0 && (
                  <>
                    <p className="text-muted-foreground">
                      Saving does not cancel these. Move, release or cancel each one here, now or from the override once it is saved.
                    </p>
                    <AffectedBookingsList
                      bookings={bookings}
                      timeZone={SITE_TIME_ZONE}
                      canManage
                      offerReassign={kind !== SITE_CLOSURE}
                      releaseNotes={(booking) => releaseNotes(booking)}
                      emptyMessage="No booked appointments fall in this window."
                    />
                  </>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">
                Appointments already booked in this window are not cancelled — check the calendar for any that need moving.
              </p>
            )}
          </>
        ) : null}
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          <IconX className="size-4" />
          Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={!canSave}>
          {saving ? <IconLoader2 className="size-4 animate-spin" /> : <IconDeviceFloppy className="size-4" />}
          Save override
        </Button>
      </div>

      <ResolutionChoiceDialog
        open={choosing}
        count={bookings.length}
        options={options}
        busy={saving}
        onChoose={choose}
        onCancel={() => setChoosing(false)}
      />

      {manualFor && <Navigate to={manualReschedulePath(manualFor)} />}

      <AutomaticReassignmentDialog
        overrideId={resolving?.id ?? null}
        bookings={resolving?.bookings}
        saved
        onClose={() => {
          setResolving(null)
          onCreated()
        }}
      />
    </section>
  )
}
